export interface PdfViewerTheme {
  body: {
    margin: string;
    fontFamily: string;
    background: string;
    color: string;
  };
  container: {
    display: string;
    flexDirection: string;
    gap: string;
    padding: string;
  };
  header: {
    display: string;
    alignItems: string;
    gap: string;
  };
  title: {
    fontSize: string;
    fontWeight: string;
  };
  details: {
    display: string;
    gridTemplateColumns: string;
    gap: string;
  };
  item: {
    display: string;
    flexDirection: string;
    gap: string;
    padding: string;
    border: string;
    borderRadius: string;
    background: string;
  };
  label: {
    fontSize: string;
    color: string;
  };
  value: {
    fontSize: string;
    color: string;
    wordBreak: string;
  };
  frameWrap: {
    height: string;
    width: string;
    border: string;
    borderRadius: string;
    overflow: string;
    background: string;
  };
  iframe: {
    width: string;
    height: string;
    border: string;
  };
}

export const darkPdfViewerTheme: PdfViewerTheme = {
  body: {
    margin: '0',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    background: '#0b0b0b',
    color: '#eaeaea',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '8px 16px',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    background: '#121212',
  },
  label: {
    fontSize: '12px',
    color: '#a0a0a0',
  },
  value: {
    fontSize: '14px',
    color: '#eaeaea',
    wordBreak: 'break-all',
  },
  frameWrap: {
    height: 'calc(100vh - 200px)',
    width: '100%',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#121212',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: '0',
  },
};

export const lightPdfViewerTheme: PdfViewerTheme = {
  body: {
    margin: '0',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    background: '#ffffff',
    color: '#1a1a1a',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '8px 16px',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: '#f7fafc',
  },
  label: {
    fontSize: '12px',
    color: '#718096',
  },
  value: {
    fontSize: '14px',
    color: '#1a1a1a',
    wordBreak: 'break-all',
  },
  frameWrap: {
    height: 'calc(100vh - 200px)',
    width: '100%',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#ffffff',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: '0',
  },
};

export const generateStylesFromTheme = (theme: PdfViewerTheme): string => {
  return `
    body {
      margin: ${theme.body.margin};
      font-family: ${theme.body.fontFamily};
      background: ${theme.body.background};
      color: ${theme.body.color};
    }
    .container {
      display: ${theme.container.display};
      flex-direction: ${theme.container.flexDirection};
      gap: ${theme.container.gap};
      padding: ${theme.container.padding};
    }
    .header {
      display: ${theme.header.display};
      align-items: ${theme.header.alignItems};
      gap: ${theme.header.gap};
    }
    .title {
      font-size: ${theme.title.fontSize};
      font-weight: ${theme.title.fontWeight};
    }
    .details {
      display: ${theme.details.display};
      grid-template-columns: ${theme.details.gridTemplateColumns};
      gap: ${theme.details.gap};
    }
    .item {
      display: ${theme.item.display};
      flex-direction: ${theme.item.flexDirection};
      gap: ${theme.item.gap};
      padding: ${theme.item.padding};
      border: ${theme.item.border};
      border-radius: ${theme.item.borderRadius};
      background: ${theme.item.background};
    }
    .label {
      font-size: ${theme.label.fontSize};
      color: ${theme.label.color};
    }
    .value {
      font-size: ${theme.value.fontSize};
      color: ${theme.value.color};
      word-break: ${theme.value.wordBreak};
    }
    .frameWrap {
      height: ${theme.frameWrap.height};
      width: ${theme.frameWrap.width};
      border: ${theme.frameWrap.border};
      border-radius: ${theme.frameWrap.borderRadius};
      overflow: ${theme.frameWrap.overflow};
      background: ${theme.frameWrap.background};
    }
    iframe {
      width: ${theme.iframe.width};
      height: ${theme.iframe.height};
      border: ${theme.iframe.border};
    }
  `;
};

export const generatedDarkStyles = generateStylesFromTheme(darkPdfViewerTheme);
export const generatedLightStyles = generateStylesFromTheme(lightPdfViewerTheme);
